import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';

type PrintType = 'offset' | 'numerique';
type Orientation = 'portrait' | 'paysage';

const PRINT_TYPE_OPTIONS: { value: PrintType; label: string }[] = [
  { value: 'offset', label: 'Offset' },
  { value: 'numerique', label: 'Numérique' },
];

const FOND_DE_PAGE_OPTIONS = ['Feuille blanche', 'Fond personnalisé'];
const GRAMMAGE_OPTIONS = ['80g/m²', '90g/m²', '120g/m²', '170g/m²'];
const TYPE_PAPIER_OPTIONS = ['Papier classique', 'Papier recyclé', 'Papier couché'];
const DIMENSION_OPTIONS = ['A4 (210x297mm)', 'A5 (148x210mm)', 'A6 (105x148mm)'];
const ENVELOPPE_OPTIONS = ['Enveloppe C4', 'Enveloppe C5', 'Enveloppe C6', 'Enveloppe à fenêtre'];

const ORIENTATION_OPTIONS: { value: Orientation; label: string }[] = [
  { value: 'portrait', label: 'Portrait' },
  { value: 'paysage', label: 'Paysage' },
];

interface CourrierCreationSectionProps {
  initialPayload?: Record<string, unknown> | null;
  onPayloadChange?: (payload: Record<string, unknown>) => void;
}

function CourrierCreationSection({ initialPayload, onPayloadChange }: CourrierCreationSectionProps) {
  const { sidebarColor } = useLayoutPreferences();

  const [printType, setPrintType] = useState<PrintType>(() => (initialPayload?.printType as PrintType) ?? 'numerique');
  const [fondDePage, setFondDePage] = useState(() => (initialPayload?.fondDePage as string) ?? '');
  const [grammage, setGrammage] = useState(() => (initialPayload?.grammage as string) ?? '');
  const [typePapier, setTypePapier] = useState(() => (initialPayload?.typePapier as string) ?? '');
  const [dimension, setDimension] = useState(() => (initialPayload?.dimension as string) ?? '');
  const [orientationRecto, setOrientationRecto] = useState<Orientation>(() => (initialPayload?.orientationRecto as Orientation) ?? 'portrait');
  const [orientationVerso, setOrientationVerso] = useState<Orientation>(() => (initialPayload?.orientationVerso as Orientation) ?? 'portrait');
  const [enveloppe, setEnveloppe] = useState(() => (initialPayload?.enveloppe as string) ?? '');

  const onPayloadChangeRef = useRef(onPayloadChange);
  onPayloadChangeRef.current = onPayloadChange;

  useEffect(() => {
    onPayloadChangeRef.current?.({
      type: 'courrier',
      printType, fondDePage, grammage, typePapier, dimension, orientationRecto, orientationVerso, enveloppe,
    });
  }, [printType, fondDePage, grammage, typePapier, dimension, orientationRecto, orientationVerso, enveloppe]);

  const cardSx = {
    border: '1px solid #e9ecef',
    borderRadius: 2,
    p: 2.5,
    mb: 2.5,
    backgroundColor: '#fff',
  };

  const sectionTitleSx = {
    fontWeight: 700,
    color: sidebarColor,
    letterSpacing: 0.3,
    mb: 2,
    fontSize: '0.88rem',
    textTransform: 'uppercase' as const,
  };

  const selectField = (
    label: string,
    value: string,
    onChange: (event: SelectChangeEvent<string>) => void,
    options: string[],
  ) => (
    <FormControl size="small" sx={{ width: 220 }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={onChange}>
        {options.map(opt => (
          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={cardSx}>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          <DescriptionIcon sx={{ fontSize: '1rem', verticalAlign: 'middle', mr: 0.75 }} />
          Document
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '0 0 auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl size="small" sx={{ width: 220 }}>
                <InputLabel>Type d&apos;impression</InputLabel>
                <Select
                  label="Type d'impression"
                  value={printType}
                  onChange={e => setPrintType(e.target.value as PrintType)}
                >
                  {PRINT_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectField('Fond de page', fondDePage, e => setFondDePage(e.target.value), FOND_DE_PAGE_OPTIONS)}
              {selectField('Grammage', grammage, e => setGrammage(e.target.value), GRAMMAGE_OPTIONS)}
              {selectField('Type de papier', typePapier, e => setTypePapier(e.target.value), TYPE_PAPIER_OPTIONS)}
              {selectField('Dimension', dimension, e => setDimension(e.target.value), DIMENSION_OPTIONS)}
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ flex: '0 0 auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl size="small" sx={{ width: 220 }}>
                <InputLabel>Orientation du Recto</InputLabel>
                <Select
                  label="Orientation du Recto"
                  value={orientationRecto}
                  onChange={e => setOrientationRecto(e.target.value as Orientation)}
                >
                  {ORIENTATION_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ width: 220 }}>
                <InputLabel>Orientation du Verso</InputLabel>
                <Select
                  label="Orientation du Verso"
                  value={orientationVerso}
                  onChange={e => setOrientationVerso(e.target.value as Orientation)}
                >
                  {ORIENTATION_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ width: 220 }}>
                <InputLabel>Enveloppe</InputLabel>
                <Select label="Enveloppe" value={enveloppe} onChange={e => setEnveloppe(e.target.value)}>
                  {ENVELOPPE_OPTIONS.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default CourrierCreationSection;
